import { useMemo, useState } from 'react'
import { Button, Chip, Table } from '@heroui/react'

import { ModuleIntro } from '@/components/layout'
import { EmptyState, ErrorState, LoadingState, Pagination } from '@/components/ui/data-state'
import { ConfirmDialog } from '@/components/ui/form-modal'
import { PlusIcon } from '@/components/ui/icons'

import {
  useAdjustmentActions,
  useAdjustmentList,
  useInventoryLocations,
  useReasonCodes,
  useStockItemsById,
} from '../hooks'
import type { Adjustment, AdjustmentLine, InventoryLocation, ReasonCode, StockItem } from '../types'
import { AdjustmentFormDialog } from './adjustment-form-dialog'
import {
  adjustmentKindLabels,
  dateTime,
  documentStatusColors,
  documentStatusLabels,
  locationLabel,
  variance,
} from './inventory-format'

const PAGE_SIZE = 25

type Step = 'submit' | 'approve' | 'cancel'

const stepCopy: Record<Step, { title: string; confirm: string; fallback: string }> = {
  submit: {
    title: 'Submit this adjustment?',
    confirm: 'Submit',
    fallback: 'Could not submit this adjustment.',
  },
  approve: {
    title: 'Approve this adjustment?',
    confirm: 'Approve and post',
    fallback: 'Could not approve this adjustment.',
  },
  cancel: {
    title: 'Cancel this adjustment?',
    confirm: 'Cancel it',
    fallback: 'Could not cancel this adjustment.',
  },
}

/**
 * Adjustments, wastage and opening balances, newest first.
 *
 * A document is saved as a draft and moves nothing. Submitting posts it — or,
 * when its value is over the business's approval threshold, leaves it waiting
 * for approval. Posted is final: a mistake is a new adjustment the other way.
 */
export function AdjustmentsPanel() {
  const [page, setPage] = useState(0)
  const [isFormOpen, setIsFormOpen] = useState(false)
  /** Bumped on each opening, so the form mounts fresh rather than holding the last draft. */
  const [formSession, setFormSession] = useState(0)
  /**
   * The step being confirmed, and the key it travels under — one per opening of
   * the confirm, so a retry after a failure is the same request.
   */
  const [pending, setPending] = useState<{
    adjustment: Adjustment
    step: Step
    key: string
  } | null>(null)

  const actions = useAdjustmentActions()
  const { adjustments, total, isLoading, isRefreshing, error, retry } = useAdjustmentList(
    page,
    PAGE_SIZE,
  )
  const { locations } = useInventoryLocations()
  const { byId: reasons } = useReasonCodes()

  const itemIds = useMemo(
    () => [
      ...new Set(
        adjustments.flatMap((adjustment) =>
          adjustment.lines.map((line) => line.itemId).filter((id): id is string => !!id),
        ),
      ),
    ],
    [adjustments],
  )
  const items = useStockItemsById(itemIds)

  function openForm() {
    setFormSession((session) => session + 1)
    setIsFormOpen(true)
  }

  function openStep(adjustment: Adjustment, step: Step) {
    actions[step].reset()
    setPending({ adjustment, step, key: crypto.randomUUID() })
  }

  function confirmStep() {
    if (!pending) return
    actions[pending.step].mutate(
      { id: pending.adjustment.id, idempotencyKey: pending.key },
      { onSuccess: () => setPending(null) },
    )
  }

  return (
    <>
      <ModuleIntro
        description="Add or remove stock, write off waste, or put opening balances. A document is a draft until it is submitted; over your approval threshold it waits for a manager."
        action={
          <Button
            variant="primary"
            size="sm"
            className="h-9 rounded-full px-4 font-medium"
            onPress={openForm}
          >
            <PlusIcon width={16} height={16} />
            New adjustment
          </Button>
        }
      />

      {isLoading ? (
        <LoadingState />
      ) : error ? (
        <ErrorState subject="your adjustments" error={error} onRetry={retry} />
      ) : adjustments.length === 0 ? (
        <EmptyState
          title="No adjustments yet."
          description="Write off what expired or spoiled, correct what was found or lost, or put opening balances."
          action={{ label: 'New adjustment', onPress: openForm }}
        />
      ) : (
        <>
          <div className={isRefreshing ? 'opacity-60 transition-opacity' : undefined}>
            <AdjustmentsTable
              adjustments={adjustments}
              locations={locations}
              items={items}
              reasons={reasons}
              onStep={openStep}
            />
          </div>

          <Pagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} />
        </>
      )}

      <AdjustmentFormDialog
        key={formSession}
        isOpen={isFormOpen}
        onOpenChange={setIsFormOpen}
        onSaved={() => {
          setIsFormOpen(false)
          setPage(0)
        }}
      />

      <ConfirmDialog
        isOpen={pending !== null}
        onOpenChange={(isOpen) => {
          if (!isOpen) setPending(null)
        }}
        title={pending ? stepCopy[pending.step].title : ''}
        body={pending && stepBody(pending.adjustment, pending.step, locations)}
        confirmLabel={pending ? stepCopy[pending.step].confirm : ''}
        onConfirm={confirmStep}
        isBusy={pending ? actions[pending.step].isPending : false}
        error={pending ? actions[pending.step].error : null}
        errorFallback={pending ? stepCopy[pending.step].fallback : undefined}
      />
    </>
  )
}

/** The confirm's words: where, how many lines, and what happens next. */
function stepBody(adjustment: Adjustment, step: Step, locations: readonly InventoryLocation[]) {
  const where = locationName(adjustment.locationId, locations)
  const count = adjustment.lines.length
  const lines = count === 1 ? 'its line' : `its ${count} lines`

  if (step === 'cancel') {
    return (
      <p>
        The {adjustmentKindLabels[adjustment.kind].toLowerCase()} at {where} is set aside and never
        moves stock.
      </p>
    )
  }
  if (step === 'approve') {
    return (
      <p>
        Stock at {where} changes by {lines}
        {adjustment.approvalValue !== null && `, worth ${adjustment.approvalValue}`}. This posts the
        stock movements and cannot be undone.
      </p>
    )
  }
  return (
    <p>
      Stock at {where} changes by {lines}, dated {dateTime(adjustment.occurredAt)}. Over your
      approval threshold it waits for a manager instead. Posted movements cannot be undone.
    </p>
  )
}

/** The location's name, or a dash for one closed since — the picker's list is active ones. */
function locationName(id: string, locations: readonly InventoryLocation[]): string {
  const location = locations.find((candidate) => candidate.id === id)
  return location ? locationLabel(location) : '—'
}

/** `Coffee beans −3 kg · Theft / unknown` — an item, what the line moves, and why. */
function lineText(
  line: AdjustmentLine,
  kind: Adjustment['kind'],
  items: ReadonlyMap<string, StockItem>,
  reasons: ReadonlyMap<string, ReasonCode>,
): string {
  const item = line.itemId ? items.get(line.itemId) : undefined
  const name = line.itemId ? (item?.name ?? '…') : `Menu item ${line.variantId}`
  // a wastage line is the amount wasted: it takes stock away
  const change = kind === 'wastage' ? -line.qty : line.qty
  const unit = item ? ` ${item.unitOfMeasure}` : line.variantId ? ' each' : ''
  const reason = reasons.get(line.reasonCodeId)?.name

  return `${name} ${variance(change)}${unit}${reason ? ` · ${reason}` : ''}`
}

interface AdjustmentsTableProps {
  adjustments: Adjustment[]
  locations: readonly InventoryLocation[]
  items: ReadonlyMap<string, StockItem>
  reasons: ReadonlyMap<string, ReasonCode>
  onStep: (adjustment: Adjustment, step: Step) => void
}

function AdjustmentsTable({
  adjustments,
  locations,
  items,
  reasons,
  onStep,
}: AdjustmentsTableProps) {
  return (
    <Table>
      <Table.ScrollContainer>
        <Table.Content aria-label="Stock adjustments">
          <Table.Header>
            <Table.Column id="date" isRowHeader>
              Happened
            </Table.Column>
            <Table.Column id="kind">Kind</Table.Column>
            <Table.Column id="location">Location</Table.Column>
            <Table.Column id="lines">Change</Table.Column>
            <Table.Column id="status">Status</Table.Column>
            <Table.Column id="actions">
              <span className="sr-only">Actions</span>
            </Table.Column>
          </Table.Header>

          <Table.Body>
            {adjustments.map((adjustment) => (
              <Table.Row key={adjustment.id} id={adjustment.id}>
                <Table.Cell>
                  <span className="font-medium tabular-nums">
                    {dateTime(adjustment.occurredAt)}
                  </span>
                </Table.Cell>

                <Table.Cell>
                  <span className="text-muted">{adjustmentKindLabels[adjustment.kind]}</span>
                </Table.Cell>

                <Table.Cell>
                  <span className="text-muted">
                    {locationName(adjustment.locationId, locations)}
                  </span>
                </Table.Cell>

                <Table.Cell>
                  <span className="flex flex-col gap-0.5 text-sm tabular-nums">
                    {adjustment.lines.map((line) => (
                      <span key={line.id}>{lineText(line, adjustment.kind, items, reasons)}</span>
                    ))}
                    {adjustment.note && (
                      <span className="text-muted text-xs">{adjustment.note}</span>
                    )}
                  </span>
                </Table.Cell>

                <Table.Cell>
                  <span className="flex flex-col items-start gap-1">
                    <Chip size="sm" variant="soft" color={documentStatusColors[adjustment.status]}>
                      {documentStatusLabels[adjustment.status]}
                    </Chip>
                    {adjustment.postedAt && (
                      <span className="text-muted text-xs tabular-nums">
                        {dateTime(adjustment.postedAt)}
                        {adjustment.selfApproved && ' · self-approved'}
                      </span>
                    )}
                    {adjustment.status === 'pending_approval' &&
                      adjustment.approvalValue !== null && (
                        <span className="text-muted text-xs tabular-nums">
                          Value {adjustment.approvalValue}
                        </span>
                      )}
                  </span>
                </Table.Cell>

                <Table.Cell>
                  <span className="flex justify-end gap-2">
                    {adjustment.status === 'draft' && (
                      <StepButton
                        adjustment={adjustment}
                        step="submit"
                        label="Submit"
                        onStep={onStep}
                      />
                    )}
                    {adjustment.status === 'pending_approval' && (
                      <StepButton
                        adjustment={adjustment}
                        step="approve"
                        label="Approve"
                        onStep={onStep}
                      />
                    )}
                    {(adjustment.status === 'draft' ||
                      adjustment.status === 'pending_approval') && (
                      <StepButton
                        adjustment={adjustment}
                        step="cancel"
                        label="Cancel"
                        onStep={onStep}
                      />
                    )}
                  </span>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Content>
      </Table.ScrollContainer>
    </Table>
  )
}

interface StepButtonProps {
  adjustment: Adjustment
  step: Step
  label: string
  onStep: (adjustment: Adjustment, step: Step) => void
}

function StepButton({ adjustment, step, label, onStep }: StepButtonProps) {
  return (
    <Button
      variant="ghost"
      size="sm"
      aria-label={`${label} the ${adjustmentKindLabels[adjustment.kind].toLowerCase()} of ${dateTime(adjustment.occurredAt)}`}
      onPress={() => onStep(adjustment, step)}
      className="border-border h-8 rounded-full border px-3 text-xs font-medium"
    >
      {label}
    </Button>
  )
}
